<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import { Lock } from 'lucide-vue-next'
import Logo from '../Components/Logo.vue'

// Sem sidebar nem topbar: quem não entrou ainda não tem menu para navegar.
defineOptions({ layout: null })

const page = usePage()
const unit = computed(() => page.props.unit ?? {})

const form = useForm({ email: '', password: '' })

// O servidor devolve uma mensagem única para e-mail inexistente e senha
// errada, de propósito: distinguir os dois revelaria quem é cliente da UP.
const submit = () => form.post('/entrar', { onFinish: () => form.reset('password') })
</script>

<template>
  <Head title="Entrar" />

  <div class="flex min-h-screen flex-col bg-up-navy px-4 py-10 font-sans">
    <main class="flex flex-1 items-center justify-center">
      <div class="w-full max-w-md">
        <div class="mb-8 text-center text-white">
          <Logo class="text-xl" />
          <p class="mt-2 text-xs tracking-[0.18em] text-white/60 uppercase">
            Confiança que faz você ir mais longe
          </p>
        </div>

        <div class="rounded-card bg-white p-6 shadow-card sm:p-8">
          <h1 class="text-lg font-semibold text-up-navy">Área do Cliente</h1>
          <p class="mt-1 text-sm text-slate-500">
            Entre para acompanhar a manutenção da sua aeronave.
          </p>

          <p
            v-if="form.errors.login"
            id="erro-login"
            role="alert"
            class="mt-5 rounded-lg bg-up-orange/10 px-4 py-3 text-sm text-up-orange-dark"
          >
            {{ form.errors.login }}
          </p>

          <form class="mt-6 space-y-4" @submit.prevent="submit">
            <div>
              <label for="email" class="block text-sm font-medium text-up-navy">E-mail</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                name="email"
                autocomplete="email"
                required
                autofocus
                :aria-describedby="form.errors.login ? 'erro-login' : undefined"
                class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-up-navy outline-none focus:border-up-orange focus:ring-2 focus:ring-up-orange/30"
              >
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-up-navy">Senha</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                name="password"
                autocomplete="current-password"
                required
                :aria-describedby="form.errors.login ? 'erro-login' : undefined"
                class="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-up-navy outline-none focus:border-up-orange focus:ring-2 focus:ring-up-orange/30"
              >
            </div>

            <button
              type="submit"
              :disabled="form.processing"
              class="flex w-full items-center justify-center rounded-lg bg-up-orange px-4 py-3.5 text-sm font-semibold tracking-wide text-white uppercase transition-colors hover:bg-up-orange-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {{ form.processing ? 'Entrando…' : 'Entrar' }}
            </button>
          </form>

          <!-- Não há redefinição automática: quem cadastra e redefine é a UP. -->
          <p class="mt-6 border-t border-slate-100 pt-5 text-sm text-slate-500">
            Esqueceu a senha ou ainda não tem acesso? Fale com a equipe
            <template v-if="unit.phone">
              pelo telefone <span class="font-medium text-up-navy">{{ unit.phone }}</span>
            </template>
            <template v-if="unit.email">
              ou por <span class="font-medium text-up-navy">{{ unit.email }}</span></template>.
          </p>
        </div>

        <p class="mt-6 flex items-center justify-center gap-2 text-xs text-white/50">
          <Lock class="h-3.5 w-3.5" aria-hidden="true" />
          Ambiente seguro · Seus dados protegidos
        </p>
      </div>
    </main>
  </div>
</template>
